/*! nosto-bigcommerce-snippet
 *  License: Copyright (c) 2024 Nosto Solutions Ltd. All Rights Reserved.
 */
import initStub from "./stub"
import { init } from "@/core/init"
import settings from "@/core/settings"
import * as tagging from "./tagging"
import * as bigCommerceAddToCart from "./cart"
import { reportAddToCart, nostojs } from "./api"
import { loadCustomer, storeCustomer } from "./customerStorage"
import { Product, Order, Customer, ProductIdentifier } from "./types"
import { SlotReference } from "@/utils/resolveSlotId"

/*
 * REPLACE_CLIENT_ID_WITH_ENV_VALU 31 characters long, just like the actual client id.
 * This means the generated sourcemap will still work.
 */
const APP_CLIENT_ID = "REPLACE_CLIENT_ID_WITH_ENV_VALU"

async function checkout(id: string) {
  const response = await fetch(`/api/storefront/order/${id}?include=payments`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  })
  const order = await response.json<Order>()
  tagging.updateOrderTagging(order)
  nostojs(api => api.loadRecommendations())
}

async function load() {
  const customer = loadCustomer()
  if (customer) {
    tagging.updateCustomerTagging(customer)
    nostojs(api => api.loadRecommendations())
    return
  }

  try {
    const response = await fetch(`/customer/current.jwt?app_client_id=${APP_CLIENT_ID}`, {
      method: "GET",
      credentials: "include"
    })
    if (!response.ok) {
      throw new Error("Customer not logged in.")
    }
    const responseText = await response.text()
    const fetchedCustomer = await fetch(`${settings.server}/bigcommerce/${settings.account}/customer`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain"
      },
      mode: "cors",
      credentials: "omit",
      body: responseText
    })
    if (!fetchedCustomer.ok) {
      throw new Error(`Unexpected status ${fetchedCustomer.status} ${fetchedCustomer.statusText}`)
    }
    const customerData = await fetchedCustomer.json<Customer>()
    storeCustomer(customerData)
    tagging.updateCustomerTagging(customerData)
  } catch (reason) {
    nostojs(api => api.internal.logger.debug(reason, "Cannot generate customer tagging."))
  } finally {
    nostojs(api => api.loadRecommendations())
  }
}

function toProductIdentifier({ productId, variantId }: Product) {
  return { productId, skuId: variantId }
}

function toProduct({ productId, skuId }: ProductIdentifier) {
  return { productId, variantId: skuId }
}

/**
 * Add multiple products to the cart
 * BigCommerce specific function with productId and variantId
 *
 * @param products
 * @param element containing slot id
 * @return {Promise} Recommendations have been reloaded and BigCommerce cart has been updated.
 */
async function addMultipleProductsToCart(products: Product[] = [], element?: SlotReference) {
  if (element) {
    await Promise.all(products.map(toProductIdentifier).map(id => reportAddToCart(id, element)))
  }
  await bigCommerceAddToCart.addMultipleProductsToCart(products)
}

/**
 * Add a single product to the cart
 * BigCommerce specific function with productId and variantId
 *
 * @param product
 * @param element containing slot id
 * @param quantity - the quantity to be added (or 1)
 * @return {Promise} Recommendations have been reloaded and BigCommerce cart has been updated.
 */
async function addProductToCart(product: Product, element?: SlotReference, quantity = 1) {
  if (element) {
    await reportAddToCart(toProductIdentifier(product), element)
  }
  await bigCommerceAddToCart.addProductToCart(product, quantity)
}

/**
 * Add a single product to the cart
 * Standard Nosto API function with productId and skuId
 *
 * @param product
 * @param element containing slot id
 * @param quantity - the quantity to be added (or 1)
 * @return {Promise} Recommendations have been reloaded and BigCommerce cart has been updated.
 */
async function addSkuToCart(product: ProductIdentifier, element?: SlotReference, quantity = 1) {
  if (element) {
    await reportAddToCart(product, element)
  }
  await bigCommerceAddToCart.addProductToCart(toProduct(product), quantity)
}

if (!window.Nosto?.bigCommerceScript) {
  initStub()

  if (typeof window.Nosto === "undefined") {
    window.Nosto = {}
  }
  Object.assign(window.Nosto, {
    // tagging related
    searchTerm: tagging.updateSearchTermTagging,
    brand: tagging.updateBrandTagging,
    category: tagging.updateCategoryTagging,
    customer: tagging.updateCustomerTagging,
    product: tagging.updateProductTagging,
    cart: tagging.updateCartTagging,
    pageType: tagging.updatePageTypeTagging,
    checkout,
    // other
    load,
    addMultipleProductsToCart,
    addProductToCart,
    addSkuToCart,
    bigCommerceScript: true
  })

  void init(window)
} else {
  console.log("Nosto Bigcommerce script already initialized")
}
